import React, { PropsWithChildren, ReactNode } from 'react';
import classnames from 'classnames';

import styles from './_PlanCard.module.css';

interface PlanCardProps {
    selected: boolean;
    onClick: VoidFunction;
    className?: string;
    badge?: ReactNode;
}

const PlanCard = ({
    selected,
    onClick,
    className,
    badge,
    children,
}: PropsWithChildren<PlanCardProps>) => (
    <div
        role="presentation"
        className={classnames(styles.root, className, {
            [styles.selected]: selected,
        })}
        onClick={onClick}
    >
        <div className={styles.overlay} />
        <div className={styles.gradient} />
        <div className={styles.content}>{children}</div>
        <div className={styles.borderLayer} />
        {badge}
    </div>
);

interface WidePlanProps {
    selected: boolean;
    onClick: VoidFunction;
    title?: string;
    subtitle?: string;
    priceText?: string;
    additionalText?: string;
    badgeText?: string;
}

export const WidePlan = ({
    selected,
    badgeText,
    title,
    subtitle,
    additionalText,
    priceText,
    onClick,
}: WidePlanProps) => (
    <PlanCard
        selected={selected}
        onClick={onClick}
        className={styles.wideMultiOffer}
        badge={
            badgeText ? (
                <div className={styles.badgeTab}>
                    <span>{badgeText}</span>
                </div>
            ) : null
        }
    >
        <div className={styles.firstLine}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>

        <div>
            <div className={styles.offerPrice}>{priceText}</div>
            <div className={styles.offerPriceSubtitle}>{additionalText}</div>
        </div>
    </PlanCard>
);

interface DefaultPlanProps {
    selected: boolean;
    onClick: VoidFunction;
    title?: string;
    subtitle?: string;
    price?: number | string;
    currency?: string;
    period?: string;
    oldPrice?: number | string;
    badgeText?: string;
}

export const DefaultPlan = ({
    selected,
    badgeText,
    title,
    subtitle,
    price,
    currency,
    period,
    onClick,
    oldPrice,
}: DefaultPlanProps) => {
    if (oldPrice !== undefined) {
        return (
            <PlanCard selected={selected} onClick={onClick} className={styles.yearOffer}>
                <div className={styles.yearTitleWrapper}>
                    <div className={classnames(styles.title)}>{title}</div>
                    {badgeText && (
                        <div className={styles.yearOfferBadge}>
                            <span className={styles.yearOfferBadgeText}>
                                <span className={styles.yearOfferBadgeTextInner}>{badgeText}</span>
                            </span>
                        </div>
                    )}
                </div>
                {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
                {price && period && (
                    <div className={styles.yearPrice}>
                        {oldPrice && (
                            <div className={styles.crossedPrice}>
                                <span>{currency}</span>
                                <span>{oldPrice}</span>
                            </div>
                        )}
                        <div className={styles.yearPriceText}>
                            <span>{currency}</span>
                            <span>{price}</span>
                        </div>
                        <span className={styles.yearPeriod}>/{period}</span>
                    </div>
                )}
            </PlanCard>
        );
    }

    return (
        <PlanCard
            selected={selected}
            onClick={onClick}
            badge={badgeText ? <div className={styles.badge}>{badgeText}</div> : null}
        >
            <div className={classnames(styles.title)}>{title}</div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            {price && period && (
                <div className={styles.price}>
                    {price}
                    {currency && (
                        <span className={styles.period}>
                            {[currency, period].filter(Boolean).join('/')}
                        </span>
                    )}
                </div>
            )}
        </PlanCard>
    );
};
